import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import DataTable from '../common/DataTable';
import { formatCurrency, formatDate, today, firstOfMonth } from '../../utils/helpers';

const reportTypes = [
  { key: 'cashbook', label: 'Cash Book' },
  { key: 'bankbook', label: 'Bank Book' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'trialbalance', label: 'Trial Balance' },
  { key: 'daybook', label: 'Day Book' },
  { key: 'expenseSummary', label: 'Expense Summary' },
  { key: 'tds', label: 'TDS Report' },
];

// Render a balance with an accounting Dr/Cr suffix (positive = Dr for asset-style accounts).
const fmtBal = (v) => `${formatCurrency(Math.abs(v || 0))} ${(v || 0) >= 0 ? 'Dr' : 'Cr'}`;

export default function AccountReports() {
  const { state } = useApp();
  const [report, setReport] = useState('cashbook');
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedParty, setSelectedParty] = useState('');
  const [selectedBank, setSelectedBank] = useState('');

  const partyName = (id) => state.parties.find(p => p.id === id)?.name || '-';
  const firstBank = state.accounts.find(a => a.type === 'Bank')?.name;
  const resolvePayAcc = (mode, bankAccount) => (mode === 'Cash' ? (state.accounts.find(a => a.type === 'Cash')?.name || 'Cash') : (bankAccount || firstBank));

  const filteredLedger = useMemo(() => {
    let entries = state.ledgerEntries;
    if (startDate) entries = entries.filter(e => e.date >= startDate);
    if (endDate) entries = entries.filter(e => e.date <= endDate);
    return entries;
  }, [state.ledgerEntries, startDate, endDate]);

  // Build a cash/bank book statement for one account, reconstructed from source vouchers
  // so each receipt/payment/expense/contra is attributed to the exact account that moved.
  const buildCashBankStatement = (accountName) => {
    const acct = state.accounts.find(a => a.name === accountName);
    const moves = [];
    state.receipts.forEach(r => {
      if (resolvePayAcc(r.mode, r.bankAccount) === accountName)
        moves.push({ date: r.date, type: 'Receipt', refNo: r.receiptNo, narration: r.narration || `Received from ${partyName(r.partyId)}`, debit: Number(r.amount) || 0, credit: 0 });
    });
    state.payments.forEach(p => {
      if (resolvePayAcc(p.mode, p.bankAccount) === accountName)
        moves.push({ date: p.date, type: 'Payment', refNo: p.paymentNo, narration: p.narration || `Paid to ${partyName(p.partyId)}`, debit: 0, credit: Number(p.amount) || 0 });
    });
    state.expenses.forEach(e => {
      const a = e.paidFrom === 'Cash' ? (state.accounts.find(x => x.type === 'Cash')?.name || 'Cash') : (e.bankAccount || firstBank);
      if (a === accountName)
        moves.push({ date: e.date, type: 'Expense', refNo: e.expenseNo, narration: e.narration || e.expenseHead, debit: 0, credit: Number(e.amount) || 0 });
    });
    state.contraEntries.forEach(c => {
      if (c.fromAccount === accountName)
        moves.push({ date: c.date, type: 'Contra', refNo: c.contraNo, narration: `Transfer to ${c.toAccount}`, debit: 0, credit: Number(c.amount) || 0 });
      if (c.toAccount === accountName)
        moves.push({ date: c.date, type: 'Contra', refNo: c.contraNo, narration: `Transfer from ${c.fromAccount}`, debit: Number(c.amount) || 0, credit: 0 });
    });
    (state.directReceipts || []).forEach(r => {
      const a = r.mode === 'Cash' ? (state.accounts.find(x => x.type === 'Cash')?.name || 'Cash') : (r.bankAccount || firstBank);
      if (a === accountName)
        moves.push({ date: r.date, type: 'Direct Receipt', refNo: r.voucherNo, narration: r.narration || `Received (${r.ledgerAccount})`, debit: Number(r.amount) || 0, credit: 0 });
    });
    (state.directPayments || []).forEach(p => {
      const a = p.mode === 'Cash' ? (state.accounts.find(x => x.type === 'Cash')?.name || 'Cash') : (p.bankAccount || firstBank);
      if (a === accountName)
        moves.push({ date: p.date, type: 'Direct Payment', refNo: p.voucherNo, narration: p.narration || `Paid (${p.ledgerAccount})`, debit: 0, credit: Number(p.amount) || 0 });
    });

    const allSorted = [...moves].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const netAll = allSorted.reduce((s, m) => s + m.debit - m.credit, 0);
    // account.balance is the current (all-time) balance → derive the original opening.
    const openingInitial = (acct?.balance || 0) - netAll;
    const openingAtStart = openingInitial + allSorted
      .filter(m => startDate && m.date < startDate)
      .reduce((s, m) => s + m.debit - m.credit, 0);
    const inRange = allSorted.filter(m => (!startDate || m.date >= startDate) && (!endDate || m.date <= endDate));
    let bal = openingAtStart;
    const rows = inRange.map(m => { bal += m.debit - m.credit; return { ...m, balance: bal }; });
    return { opening: openingAtStart, rows, closing: bal };
  };

  const statementColumns = [
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'type', label: 'Type', render: (v) => <span className="badge badge-gray">{v}</span> },
    { key: 'refNo', label: 'Ref No' },
    { key: 'narration', label: 'Narration' },
    { key: 'debit', label: 'Debit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
    { key: 'credit', label: 'Credit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
    { key: 'balance', label: 'Balance', align: 'right', render: (v) => fmtBal(v), exportValue: (r) => r.balance },
  ];

  const Statement = ({ title, accountName, exportName }) => {
    const { opening, rows, closing } = buildCashBankStatement(accountName);
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-gray-700">{title}: {accountName || '-'}</span>
          <span className="text-sm font-semibold text-gray-600">Opening Balance: {fmtBal(opening)}</span>
        </div>
        <DataTable columns={statementColumns} data={rows} searchFields={['refNo', 'narration']} exportFilename={exportName} />
        <div className="flex justify-end mt-3 pt-3 border-t border-gray-200">
          <span className="font-bold text-gray-900">Closing Balance: {fmtBal(closing)}</span>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    switch (report) {
      case 'cashbook': {
        const cashName = state.accounts.find(a => a.type === 'Cash')?.name || 'Cash';
        return <Statement title="Cash Account" accountName={cashName} exportName="CashBook" />;
      }

      case 'bankbook': {
        const banks = state.accounts.filter(a => a.type === 'Bank');
        const bankName = selectedBank || banks[0]?.name || '';
        return (
          <div>
            <div className="mb-4 max-w-xs">
              <label className="label">Bank Account</label>
              <select className="input" value={bankName} onChange={e => setSelectedBank(e.target.value)}>
                {banks.length === 0 && <option value="">No bank accounts</option>}
                {banks.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            {bankName
              ? <Statement title="Bank Account" accountName={bankName} exportName={`BankBook_${bankName}`} />
              : <p className="text-gray-400 text-sm">Add a Bank-type account in Account Master to use the Bank Book.</p>}
          </div>
        );
      }

      case 'ledger': {
        const single = selectedParty || selectedAccount;
        const selAcc = selectedAccount ? state.accounts.find(a => a.name === selectedAccount) : null;
        const isCashBankAcc = selAcc && (selAcc.type === 'Cash' || selAcc.type === 'Bank');

        let opening = 0, closing = 0, rows = filteredLedger;
        if (isCashBankAcc) {
          // Cash/Bank account → reconstruct from source vouchers so opening balance and
          // Dr/Cr direction match the Cash Book / Bank Book.
          const st = buildCashBankStatement(selectedAccount);
          opening = st.opening;
          closing = st.closing;
          rows = st.rows.map(r => ({ ...r, account: selectedAccount }));
        } else if (single) {
          const base = selectedParty
            ? state.ledgerEntries.filter(e => e.partyId === selectedParty)
            : state.ledgerEntries.filter(e => e.account === selectedAccount);
          const sorted = [...base].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
          let initial = 0;
          if (selectedParty) {
            const p = state.parties.find(x => x.id === selectedParty);
            if (p) initial = p.balanceType === 'Cr' ? -(Number(p.openingBalance) || 0) : (Number(p.openingBalance) || 0);
          }
          opening = initial + sorted
            .filter(e => startDate && e.date < startDate)
            .reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);
          const inRange = sorted.filter(e => (!startDate || e.date >= startDate) && (!endDate || e.date <= endDate));
          let bal = opening;
          rows = inRange.map(e => { bal += (e.debit || 0) - (e.credit || 0); return { ...e, balance: bal }; });
          closing = bal;
        }

        const ledgerCols = [
          { key: 'date', label: 'Date', render: (v) => formatDate(v) },
          { key: 'type', label: 'Type', render: (v) => <span className="badge badge-gray">{v}</span> },
          { key: 'refNo', label: 'Ref No' },
          { key: 'account', label: 'Account' },
          { key: 'narration', label: 'Narration' },
          { key: 'debit', label: 'Debit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
          { key: 'credit', label: 'Credit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
          ...(single ? [{ key: 'balance', label: 'Balance', align: 'right', render: (v) => fmtBal(v), exportValue: (r) => r.balance }] : []),
        ];

        return (
          <div>
            <div className="flex flex-wrap gap-4 mb-4 items-end">
              <div>
                <label className="label">Account</label>
                <select className="input" value={selectedAccount} onChange={e => { setSelectedAccount(e.target.value); if (e.target.value) setSelectedParty(''); }}>
                  <option value="">-- Select Account --</option>
                  {state.accounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>
              {single && <div className="ml-auto text-sm font-semibold text-gray-600 self-center">Opening Balance: {fmtBal(opening)}</div>}
            </div>
            {single ? (
              <>
                <DataTable columns={ledgerCols} data={rows} searchFields={['refNo', 'narration', 'account']} exportFilename="Account_ledger" />
                <div className="flex justify-end mt-3 pt-3 border-t border-gray-200">
                  <span className="font-bold text-gray-900">Closing Balance: {fmtBal(closing)}</span>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500 py-12">Select an account to view its ledger.</div>
            )}
          </div>
        );
      }

      case 'trialbalance': {
        const trialData = state.accounts.map(acc => {
          const entries = filteredLedger.filter(e => e.account === acc.name);
          const totalDebit = entries.reduce((s, e) => s + (e.debit || 0), 0);
          const totalCredit = entries.reduce((s, e) => s + (e.credit || 0), 0);
          return {
            account: acc.name,
            type: acc.type,
            debit: totalDebit + (acc.type === 'Cash' || acc.type === 'Bank' ? acc.balance : 0),
            credit: totalCredit,
          };
        }).filter(a => a.debit > 0 || a.credit > 0);
        const totDr = trialData.reduce((s, a) => s + a.debit, 0);
        const totCr = trialData.reduce((s, a) => s + a.credit, 0);
        return (
          <div>
            <DataTable columns={[
              { key: 'account', label: 'Account', sortable: true },
              { key: 'type', label: 'Type' },
              { key: 'debit', label: 'Debit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
              { key: 'credit', label: 'Credit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
            ]} data={trialData} searchFields={['account']} exportFilename="Account_trialbalance" />
            <div className="flex justify-end gap-8 mt-4 font-bold text-sm">
              <span>Total Debit: {formatCurrency(totDr)}</span>
              <span>Total Credit: {formatCurrency(totCr)}</span>
            </div>
          </div>
        );
      }

      case 'daybook': {
        const allTxns = [
          ...state.salesInvoices.map(i => ({ date: i.date, type: 'Sales', ref: i.invoiceNo, party: state.parties.find(p => p.id === i.partyId)?.name, debit: i.grandTotal, credit: 0 })),
          ...state.purchaseBills.map(b => ({ date: b.date, type: 'Purchase', ref: b.entryNo, party: state.parties.find(p => p.id === b.partyId)?.name, debit: 0, credit: b.grandTotal })),
          ...state.receipts.map(r => ({ date: r.date, type: 'Receipt', ref: r.receiptNo, party: state.parties.find(p => p.id === r.partyId)?.name, debit: r.amount, credit: 0 })),
          ...state.payments.map(p => ({ date: p.date, type: 'Payment', ref: p.paymentNo, party: state.parties.find(pr => pr.id === p.partyId)?.name, debit: 0, credit: p.amount })),
          ...state.expenses.map(e => ({ date: e.date, type: 'Expense', ref: e.expenseNo, party: e.expenseHead, debit: 0, credit: e.amount })),
        ].filter(t => {
          if (startDate && t.date < startDate) return false;
          if (endDate && t.date > endDate) return false;
          return true;
        }).sort((a, b) => (a.date || '').localeCompare(b.date || ''));

        return <DataTable columns={[
          { key: 'date', label: 'Date', render: (v) => formatDate(v) },
          { key: 'type', label: 'Type', render: (v) => <span className="badge badge-gray">{v}</span> },
          { key: 'ref', label: 'Ref No' },
          { key: 'party', label: 'Party/Head' },
          { key: 'debit', label: 'Debit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
          { key: 'credit', label: 'Credit', align: 'right', render: (v) => v > 0 ? formatCurrency(v) : '-' },
        ]} data={allTxns} searchFields={['ref', 'party']} />;
      }

      case 'expenseSummary': {
        const map = {};
        const filtered = state.expenses.filter(e => {
          if (startDate && e.date < startDate) return false;
          if (endDate && e.date > endDate) return false;
          return true;
        });
        filtered.forEach(e => {
          if (!map[e.expenseHead]) map[e.expenseHead] = { head: e.expenseHead, count: 0, total: 0 };
          map[e.expenseHead].count++;
          map[e.expenseHead].total += e.amount;
        });
        return <DataTable columns={[
          { key: 'head', label: 'Expense Head', sortable: true },
          { key: 'count', label: 'Entries', align: 'right' },
          { key: 'total', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={Object.values(map).sort((a, b) => b.total - a.total)} searchFields={['head']} />;
      }

      case 'tds': {
        const inRange = (d) => (!startDate || d >= startDate) && (!endDate || d <= endDate);
        const mk = (date, ref, nature, kind, section, gross, rate, tds, net) => ({ date, ref, nature, kind, section, gross, rate, tds, net });
        const rows = [
          ...state.expenses.filter(e => Number(e.tdsAmount) > 0 && inRange(e.date))
            .map(e => mk(e.date, e.expenseNo, e.expenseHead, 'Payable', e.tdsSection || '-', e.amount, e.tdsRate || 0, e.tdsAmount || 0, e.netAmount != null ? e.netAmount : e.amount - (e.tdsAmount || 0))),
          ...state.purchaseBills.filter(b => Number(b.tdsAmount) > 0 && inRange(b.date))
            .map(b => mk(b.date, b.entryNo, 'Purchase', 'Payable', b.tdsSection || '-', b.grandTotal, b.tdsRate || 0, b.tdsAmount || 0, b.netAmount != null ? b.netAmount : b.grandTotal - (b.tdsAmount || 0))),
          ...state.salesInvoices.filter(i => Number(i.tdsAmount) > 0 && inRange(i.date))
            .map(i => mk(i.date, i.invoiceNo, 'Sales', 'Receivable', i.tdsSection || '-', i.grandTotal, i.tdsRate || 0, i.tdsAmount || 0, i.netAmount != null ? i.netAmount : i.grandTotal - (i.tdsAmount || 0))),
        ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        const totalPayable = rows.filter(r => r.kind === 'Payable').reduce((s, r) => s + r.tds, 0);
        const totalReceivable = rows.filter(r => r.kind === 'Receivable').reduce((s, r) => s + r.tds, 0);
        const bal = (name) => { const a = (state.accounts || []).find(x => x.name === name); return a ? Math.abs(a.balance || 0) : 0; };
        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="card"><p className="text-xs text-gray-500">TDS Deducted by us (Payable)</p><p className="text-xl font-bold text-red-600">{formatCurrency(totalPayable)}</p></div>
              <div className="card"><p className="text-xs text-gray-500">TDS Deducted on our income (Receivable)</p><p className="text-xl font-bold text-green-700">{formatCurrency(totalReceivable)}</p></div>
              <div className="card"><p className="text-xs text-gray-500">Balances</p><p className="text-sm font-semibold text-red-600">Payable: {formatCurrency(bal('TDS Payable'))}</p><p className="text-sm font-semibold text-green-700">Receivable: {formatCurrency(bal('TDS Receivable'))}</p></div>
            </div>
            <DataTable columns={[
              { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: r => formatDate(r.date) },
              { key: 'ref', label: 'Voucher No' },
              { key: 'nature', label: 'Nature' },
              { key: 'kind', label: 'Type', render: (v) => <span className={`badge ${v === 'Receivable' ? 'badge-green' : 'badge-gray'}`}>{v}</span> },
              { key: 'section', label: 'Section', render: (v) => <span className="font-mono text-blue-700">{v}</span> },
              { key: 'gross', label: 'Gross', align: 'right', render: (v) => formatCurrency(v) },
              { key: 'rate', label: 'Rate', align: 'right', render: (v) => `${v}%` },
              { key: 'tds', label: 'TDS', align: 'right', render: (v) => formatCurrency(v) },
              { key: 'net', label: 'Net', align: 'right', render: (v) => formatCurrency(v) },
            ]} data={rows} searchFields={['ref', 'nature', 'section']} exportFilename="tds-report"
              emptyState={{ title: 'No TDS records', description: 'TDS on expenses, purchase bills and sales invoices in the selected period will appear here.' }} />
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <div>
      <h1 className="page-title mb-6">Account Reports</h1>
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">Report Type</label>
            <select className="input" value={report} onChange={e => setReport(e.target.value)}>
              {reportTypes.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="card">{(() => {
        const el = renderReport();
        return el && el.type === DataTable ? React.cloneElement(el, { exportFilename: `Account_${report}` }) : el;
      })()}</div>
    </div>
  );
}
